import { UiSchema } from '@rjsf/utils';

type TSchemaNode = Record<string, unknown> & {
  type?: unknown;
  description?: string;
  properties?: Record<string, TSchemaNode>;
  items?: TSchemaNode | TSchemaNode[];
  dependencies?: Record<string, { oneOf?: TSchemaNode[] } | unknown>;
  enum?: unknown[];
};

type TUiNode = Record<string, unknown>;

export type TStrategyDescriptions = Record<string, string>;

const STRATEGY_SEPARATOR = '->';
const STRATEGY_KEY = /^[A-Z][A-Z0-9_]*$/;

const parseStrategyLine = (line: string): [string, string] | null => {
  const separatorIndex = line.indexOf(STRATEGY_SEPARATOR);
  if (separatorIndex === -1) return null;
  const key = line.slice(0, separatorIndex).trim();
  const value = line.slice(separatorIndex + STRATEGY_SEPARATOR.length).trim();
  return STRATEGY_KEY.test(key) && value ? [key, value] : null;
};

export const parseStrategyDescriptions = (description?: string): TStrategyDescriptions => {
  if (!description) return {};
  return Object.fromEntries(
    description
      .split('\n')
      .map(parseStrategyLine)
      .filter((entry): entry is [string, string] => entry !== null),
  );
};

const stripStrategyLines = (description: string): string =>
  description
    .split('\n')
    .filter((line) => parseStrategyLine(line) === null)
    .join('\n')
    .trim();

const isObjectNode = (value: unknown): value is TSchemaNode =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

const RULE_IDENTIFIER_PATTERN = /^(fieldIdentifier|[a-z]\w*FieldIdentifier)$/;
const WORKFLOW_IDS_PATTERN = /^(workflowIds|[a-z]\w*WorkflowIds)$/;
const ORGANIZATION_ID_PATTERN = /^[a-z]\w*OrganizationId$/;

export const getRuleIdentifierKey = (node: TSchemaNode): string | null => {
  if (!isObjectNode(node.items)) return null;
  const itemProperties = node.items.properties ?? {};
  if (!Array.isArray(itemProperties.operator?.enum)) return null;
  return Object.keys(itemProperties).find((key) => RULE_IDENTIFIER_PATTERN.test(key)) ?? null;
};

export const isRuleListSchema = (node: TSchemaNode): boolean => getRuleIdentifierKey(node) !== null;

const PRIMITIVE_TYPES = new Set(['string', 'number', 'integer', 'boolean']);

const applyReferenceUi = (key: string, node: TSchemaNode, ui: TUiNode): void => {
  if (isObjectNode(node.items) && PRIMITIVE_TYPES.has(String(node.items.type))) {
    ui.items = { ...childUi(ui, 'items'), 'ui:label': false };
  }
  if (isObjectNode(node.items) && Array.isArray(node.items.enum)) {
    ui['ui:field'] = 'EnumChipsField';
  }
  if (WORKFLOW_IDS_PATTERN.test(key) && isObjectNode(node.items) && node.items.type === 'string') {
    ui.items = { ...childUi(ui, 'items'), 'ui:widget': 'WorkflowReferenceWidget' };
  }
  if (ORGANIZATION_ID_PATTERN.test(key) && node.type === 'string') {
    ui['ui:widget'] = 'OrganizationReferenceWidget';
  }
};

const childUi = (ui: TUiNode, key: string): TUiNode => {
  if (!isObjectNode(ui[key])) ui[key] = {};
  return ui[key] as TUiNode;
};

const walk = (node: TSchemaNode, ui: TUiNode, parent?: TSchemaNode): void => {
  const ruleIdentifierKey = getRuleIdentifierKey(node);
  if (ruleIdentifierKey) {
    ui['ui:field'] = 'RuleListField';
    ui['ui:options'] = { identifierKey: ruleIdentifierKey };
    return;
  }

  if (isObjectNode(node.items)) {
    walk(node.items, childUi(ui, 'items'), node);
  }

  const strategy = node.properties?.strategy;
  if (strategy && Array.isArray(strategy.enum)) {
    const ownDescriptions = parseStrategyDescriptions(node.description);
    const hasOwn = Object.keys(ownDescriptions).length > 0;
    const describedNode = hasOwn ? node : parent;
    const descriptions = hasOwn ? ownDescriptions : parseStrategyDescriptions(parent?.description);
    ui.strategy = { ...childUi(ui, 'strategy'), 'ui:widget': 'StrategyCardsWidget', 'ui:options': { descriptions } };
    if (describedNode?.description && Object.keys(descriptions).length > 0) {
      describedNode.description = stripStrategyLines(describedNode.description);
    }
  }

  Object.entries(node.properties ?? {}).forEach(([key, child]) => {
    if (key === 'strategy' || !isObjectNode(child)) return;
    applyReferenceUi(key, child, childUi(ui, key));
    walk(child, childUi(ui, key), node);
  });

  Object.values(node.dependencies ?? {}).forEach((dependency) => {
    if (!isObjectNode(dependency) || !Array.isArray(dependency.oneOf)) return;
    dependency.oneOf.forEach((variant: TSchemaNode) => {
      Object.entries(variant.properties ?? {}).forEach(([key, child]) => {
        if (key === 'strategy' || !isObjectNode(child)) return;
        applyReferenceUi(key, child, childUi(ui, key));
        walk(child, childUi(ui, key), node);
      });
    });
  });
};

export const enhanceSchema = (schema: object): { schema: object; uiSchema: UiSchema } => {
  const cloned = structuredClone(schema) as TSchemaNode;
  const uiSchema: TUiNode = {};
  walk(cloned, uiSchema);
  return { schema: cloned, uiSchema: uiSchema as UiSchema };
};
